/**
 * A number `isNonZero` has checked: set, not 0 and not NaN. The brand makes the `false` branch keep `number` (a
 * `value is T` guard would drop it there, though 0 lands there). It is a plain, optional property: plain so a
 * declaration carrying the narrowed type (an exported server function returning a checked value) can write it out
 * (a `unique symbol` from another package cannot be named there, TS4058); optional so the narrowed value still
 * compares with other branded values and a `let` copy of it still takes plain numbers.
 */
export type NonZeroNumber = number & { readonly __nonZeroChecked?: true };

/**
 * True when `value` is a number other than 0 (and NaN): what truthiness does to a number that may be absent, said
 * explicitly. Use it where 0 means "none" (a count, an amount, a timestamp). Where 0 is a real value (an index, a
 * position, a coordinate), compare with `undefined` / `null` instead. The lint rule
 * `number-condition/no-number-truthiness` (packages/web/eslint-plugins) asks for one of the two.
 *
 * The narrowed type is branded (`NonZeroNumber`), so the `false` branch keeps `number` (it may hold 0).
 */
export function isNonZero<T extends number>(
  value: T | null | undefined,
): value is T & NonZeroNumber {
  return (
    value !== undefined && value !== null && value !== 0 && !Number.isNaN(value)
  );
}

/**
 * The first of `values` that `isNonZero` accepts, else `undefined`: a `a || b || c` chain of numbers where 0 means "not
 * set", said explicitly (`firstNonZero(a, b, c) ?? 0`). Every value is evaluated, so pass reads, not work.
 */
export function firstNonZero(
  ...values: readonly (number | null | undefined)[]
): number | undefined {
  return values.find((value): value is number => isNonZero(value));
}

/** A `string | number` that `isNonEmpty` has checked: set, not "", not 0 and not NaN. Branded like `NonZeroNumber`. */
export type NonEmptyValue = { readonly __nonEmptyChecked?: true };

/**
 * True when `value` is set and is not "", 0 or NaN: what truthiness does to a value that may be a string or a number
 * (an id, a serial, a field read from a form or a grid), said explicitly. Use it where both "" and 0 mean "none"; where
 * 0 is a real value, compare with `null` / `undefined` / `""` instead. Prefer it to a hand-written
 * `x !== undefined && x !== "" && x !== 0`, which lets NaN and the other nullish through (the lint rule
 * `number-condition/no-number-truthiness` reports that chain).
 * Also takes a `symbol` (a column `field` typed `keyof Row`), which is always set.
 *
 * The narrowed type is branded like `isNonZero`'s, so the `false` branch keeps `string | number`.
 */
export function isNonEmpty<T extends PropertyKey>(
  value: T | null | undefined,
): value is T & NonEmptyValue {
  return (
    value !== undefined &&
    value !== null &&
    value !== "" &&
    value !== 0 &&
    !Number.isNaN(value)
  );
}
